<template>
  <Teleport to="body">
    <Transition name="panel">
      <aside
        v-if="open && def"
        class="metric-panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`mp-title-${def.id}`"
        data-test="metric-panel"
        @keydown.esc="close"
      >
        <div class="metric-panel-backdrop" @click="close" />
        <section class="metric-panel-card" tabindex="-1" ref="cardRef">
          <header class="metric-panel-head">
            <div>
              <div class="metric-panel-eyebrow mono">{{ def.id }}</div>
              <h2 :id="`mp-title-${def.id}`" class="metric-panel-title">{{ def.label }}</h2>
            </div>
            <button class="iconbtn" type="button" aria-label="Close panel" title="Close (Esc)" @click="close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div class="metric-panel-body">
            <div class="mp-current">
              <div class="mp-current-label">Current value</div>
              <div class="mp-current-row">
                <div class="mp-current-value">{{ valueDisplay }}</div>
                <span :class="['mp-rag-pill', ragClass]">{{ ragClass.toUpperCase() }}</span>
              </div>
            </div>

            <section v-if="def.details.whyMatters" class="mp-section">
              <h3>Why it matters</h3>
              <p>{{ def.details.whyMatters }}</p>
            </section>

            <section v-if="def.details.formula" class="mp-section">
              <h3>Formula</h3>
              <pre class="mp-formula">{{ def.details.formula }}</pre>
            </section>

            <section v-if="def.details.bands" class="mp-section">
              <h3>RAG bands</h3>
              <p>{{ def.details.bands }}</p>
            </section>

            <section v-if="def.details.source" class="mp-section">
              <h3>Source</h3>
              <span :class="['mp-source-chip', def.details.source]">{{ def.details.source }}</span>
            </section>

            <section v-if="def.details.actions?.length" class="mp-section">
              <h3>What to do</h3>
              <ul class="mp-actions">
                <li v-for="(a, i) in def.details.actions" :key="i">{{ a }}</li>
              </ul>
            </section>

            <p class="mp-foot subtle">Thresholds are provisional pending S27 calibration.</p>
          </div>
        </section>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import type { MetricEnvelope } from "@/api/types";
import type { MetricDef } from "@/lib/metricDefs";
import { fmtInt, fmtNum, pct } from "@/lib/format";

const props = defineProps<{
  open: boolean;
  def: MetricDef | null;
  envelope: MetricEnvelope | null | undefined;
}>();

const emit = defineEmits<{ (e: "close"): void }>();

const cardRef = ref<HTMLElement | null>(null);

const ragClass = computed(() => props.envelope?.rag ?? "none");

const valueDisplay = computed(() => {
  const def = props.def;
  const m = props.envelope;
  if (!def || !m || m.value == null) return "—";
  const v = m.value;
  switch (def.kind) {
    case "pct":
      return typeof v === "number" ? pct(v) : "—";
    case "int":
      return typeof v === "number" ? fmtInt(v) : "—";
    case "days":
      return typeof v === "number" ? `${fmtNum(v, 1)} d` : "—";
    case "bool":
      return v === true ? "READY" : "NOT READY";
    case "ratio3":
      if (Array.isArray(v)) return v.map((n) => pct(Number(n))).join(" / ");
      return "—";
    case "share": {
      if (typeof v !== "object" || v === null || Array.isArray(v)) return "—";
      const obj = v as Record<string, number>;
      const sp = obj.sp ?? null;
      const sh = obj.share ?? null;
      const left = sp == null ? "—" : fmtInt(sp);
      const right = sh == null ? "" : `  ·  ${pct(sh)}`;
      return `${left}${right}`;
    }
    case "spPct": {
      if (typeof v !== "object" || v === null || Array.isArray(v)) return "—";
      const obj = v as Record<string, number>;
      const sp = obj.sp ?? obj.SP ?? null;
      const p = obj.pct ?? obj.percent ?? null;
      const left = sp == null ? "—" : fmtInt(sp);
      const right = p == null ? "" : `  ·  ${pct(p)}`;
      return `${left}${right}`;
    }
    case "dict": {
      if (typeof v !== "object" || v === null || Array.isArray(v)) return "—";
      const items = Object.entries(v as Record<string, number>)
        .slice(0, 4)
        .map(([k, n]) => {
          if (typeof n !== "number") return `${k}: ${n}`;
          return `${k}: ${def.unit === "d" ? `${fmtNum(n, 1)}d` : fmtInt(n)}`;
        });
      return items.length ? items.join(" · ") : "—";
    }
    default:
      try { return JSON.stringify(v).slice(0, 60); } catch { return "—"; }
  }
});

function close() { emit("close"); }

watch(
  () => props.open,
  async (v) => {
    if (v) {
      await nextTick();
      cardRef.value?.focus();
    }
  },
);
</script>

<style scoped>
.metric-panel { position: fixed; inset: 0; z-index: 50; }
.metric-panel-backdrop {
  position: absolute; inset: 0; background: rgba(15, 23, 42, 0.45);
}
.metric-panel-card {
  position: absolute; left: 0; top: 0; bottom: 0;
  width: min(440px, 92vw);
  background: var(--surface-card, #fff);
  border-right: 1px solid var(--surface-border, #e5e7eb);
  box-shadow: 8px 0 24px rgba(0,0,0,0.12);
  display: flex; flex-direction: column;
  outline: none;
}
.metric-panel-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--surface-border, #e5e7eb);
}
.metric-panel-eyebrow { font-size: 11px; color: var(--text-muted, #6b7280); }
.metric-panel-title { margin: 4px 0 0; font-size: 18px; font-weight: 600; }
.iconbtn {
  background: transparent; border: 0; cursor: pointer;
  width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280); border-radius: 6px;
}
.iconbtn:hover { background: var(--surface-section, #f3f4f6); color: var(--text-color, #111827); }

.metric-panel-body { padding: 20px; overflow: auto; display: flex; flex-direction: column; gap: 18px; }

.mp-current {
  background: var(--surface-section, #f9fafb); border-radius: 8px; padding: 12px 14px;
  border: 1px solid var(--surface-border, #e5e7eb);
}
.mp-current-label { font-size: 11px; text-transform: uppercase; color: var(--text-muted, #6b7280); letter-spacing: 0.05em; }
.mp-current-row { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; }
.mp-current-value { font-size: 22px; font-weight: 700; }
.mp-rag-pill {
  display: inline-flex; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600;
  border: 1px solid currentColor;
}
.mp-rag-pill.green  { color: #15803d; background: #dcfce7; border-color: #86efac; }
.mp-rag-pill.amber  { color: #92400e; background: #fef3c7; border-color: #fcd34d; }
.mp-rag-pill.red    { color: #991b1b; background: #fee2e2; border-color: #fca5a5; }
.mp-rag-pill.none   { color: #6b7280; background: #f3f4f6; border-color: #e5e7eb; }

.mp-section h3 {
  margin: 0 0 6px; font-size: 11px; text-transform: uppercase;
  color: var(--text-muted, #6b7280); letter-spacing: 0.05em; font-weight: 600;
}
.mp-section p { margin: 0; font-size: 14px; line-height: 1.5; }
.mp-formula {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px;
  background: var(--surface-section, #f3f4f6); padding: 10px 12px; border-radius: 6px;
  white-space: pre-wrap; margin: 0;
}
.mp-source-chip {
  display: inline-flex; padding: 2px 10px; border-radius: 999px; font-size: 12px;
  border: 1px solid var(--surface-border, #e5e7eb);
}
.mp-source-chip.field { background: #ecfeff; color: #0e7490; border-color: #67e8f9; }
.mp-source-chip.changelog { background: #fdf4ff; color: #86198f; border-color: #f0abfc; }
.mp-actions { margin: 0; padding-left: 18px; }
.mp-actions li { margin: 4px 0; font-size: 14px; line-height: 1.45; }
.mp-foot { font-size: 12px; }
.subtle { color: var(--text-muted, #6b7280); }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

/* Slide-in transition */
.panel-enter-active, .panel-leave-active { transition: opacity 0.18s ease; }
.panel-enter-active .metric-panel-card,
.panel-leave-active .metric-panel-card { transition: transform 0.22s ease; }
.panel-enter-from, .panel-leave-to { opacity: 0; }
.panel-enter-from .metric-panel-card,
.panel-leave-to .metric-panel-card { transform: translateX(-100%); }
</style>
